import React from 'react';
import ReactDOM from 'react-dom';
import { observer } from 'mobx-react';
import { observable, action, makeObservable } from 'mobx';
import { ListChildComponentProps, FixedSizeList as List } from 'react-window';
import { Label } from 'reactstrap';
import { FaFilter, FaSearch } from 'react-icons/fa';

import './filter-popup.scss';
import {
    gridStringSearchService,
    stringSearchService, TextMatchComparer
} from '@Services';
import {Checkbox} from '@Components/FormControls';

export type ComparisonOperator = '>' | '<' | '=' | '!=';

export class ColumnSort {
    public static readonly ASC: string = 'asc';
    public static readonly DESC: string = 'desc';
}
export class ColumnFilters {
    public static readonly WithValues: string = 'with values';
    public static readonly WithoutValues: string = 'without values';
}

export type FilterValue = string | number | ColumnFilters;
export type FilterOption = | { name: string | number, value: FilterValue, isGroup?: boolean, children?: FilterOption[] };
export type ColumnFiltersMap = Map<string, FilterValue[]>;
export type ColumnSearchMap = Map<string, string>;
export type ColumnSortModel = { sortType: ColumnSort, key: string };

type FilterPopupProps = {
    dataField: string;
    filterOptions?: FilterOption[] | (() => FilterOption[])
    isFilterActive: boolean;
    disableSorting?: boolean;
    hasEmptyValues: boolean;
    columnSort: ColumnSortModel | null;
    columnFilters: ColumnFiltersMap;
    columnSearch: string;
    onChangeSort: (key: string, sortType: ColumnSort) => void;
    onChangeFilters: (key: string, filterValue: FilterValue) => void;
    onChangeSearch: (key: string, searchValue: string) => void;
    onChangeManyFilters: (key: string, filterValues: FilterValue[], add: boolean) => void;
    onOpen?: (dataField: string) => void;
    onClose?: () => void;
};

type GroupCheckboxProps = { checked: boolean; indeterminate: boolean; onChange: () => void };
const GroupCheckbox = ({ checked, indeterminate, onChange }: GroupCheckboxProps) => {
    const ref = React.useRef<HTMLInputElement>(null);
    React.useEffect(() => {
        if (ref.current) ref.current.indeterminate = indeterminate;
    }, [indeterminate]);
    return (
        <Checkbox
            innerRef={ref}
            checked={checked}
            onChange={onChange}
            onClick={(e) => e.stopPropagation()}
            style={{ width: 'unset', height: 'unset', flexShrink: 0 }}
        />
    );
};

const COMPARISON_OPERATORS: ComparisonOperator[] = ['>', '<', '=', '!='];
const OPERATOR_LABELS: Record<ComparisonOperator, string> = { '>': '>', '<': '<', '=': '=', '!=': '≠' };
const OPERATOR_TITLES: Record<ComparisonOperator, string> = {
    '>': 'Greater than', '<': 'Less than', '=': 'Equal to', '!=': 'Not equal to'
};

@observer
export class FilterPopup extends React.Component<FilterPopupProps, {}> {
    @observable private _isVisible: boolean = false;
    @observable private _popupEvent: React.MouseEvent | null = null;
    @observable private _adjustedLeft: number | null = null;
    @observable private _expandedGroups: Set<string> = new Set();
    @observable private _activeOperator: ComparisonOperator | null = null;
    @observable private _comparisonValue: string = '';
    @observable private _isOperatorMenuVisible: boolean = false;
    @observable private _operatorMenuPos: { top: number; left: number; width: number } | null = null;
    private _popupWrapperRef: React.RefObject<HTMLDivElement> = React.createRef();
    private _searchWrapperRef: React.RefObject<HTMLDivElement> = React.createRef();
    private _operatorMenuRef: React.RefObject<HTMLDivElement> = React.createRef();

    constructor(props: FilterPopupProps) {
        super(props);
        makeObservable(this);
    }

    componentDidMount() {
        document.addEventListener('mousedown', this._handleClickOutside);
        window.addEventListener('resize', this._onDisappear);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this._handleClickOutside);
        window.removeEventListener('resize', this._onDisappear);
    }

    componentDidUpdate() {
        if (this._isVisible) {
            this._adjustPopupPosition();
        }
    }

    @action.bound
    private _adjustPopupPosition() {
        const el = this._popupWrapperRef.current;
        if (!el) return;

        const margin = 5;
        const rect = el.getBoundingClientRect();
        let desiredLeft = rect.left;

        if (rect.right > window.innerWidth - margin) {
            desiredLeft = window.innerWidth - rect.width - margin;
        }
        if (desiredLeft < margin) {
            desiredLeft = margin;
        }

        if (Math.abs(desiredLeft - rect.left) > 0.5) {
            this._adjustedLeft = desiredLeft;
        }
    }

    @action.bound
    private _onDisappear() {
        this._isVisible = false;
        this._adjustedLeft = null;
        this._activeOperator = null;
        this._comparisonValue = '';
        this._isOperatorMenuVisible = false;
        this.props.onClose?.();
    }

    @action.bound
    private _handleClickOutside(event: MouseEvent) {
        const target = event.target as Node;
        const inPopup = this._popupWrapperRef.current?.contains(target);
        const inMenu = this._operatorMenuRef.current?.contains(target);

        if (this._isVisible && !inPopup && !inMenu) {
            this._onDisappear();
            return;
        }
        if (this._isOperatorMenuVisible && !inMenu && !this._searchWrapperRef.current?.contains(target)) {
            this._isOperatorMenuVisible = false;
        }
    }

    render() {
        const clickX = this._popupEvent?.clientX || 100;
        const clickY = this._popupEvent?.clientY || 100;

        const screenW = window.innerWidth;
        const popupWidth = 120;

        let left = clickX + 5;
        let top = clickY + 5;

        if ((screenW - clickX) < popupWidth) {
            left = clickX - popupWidth - 5;
        }

        if (this._adjustedLeft !== null) {
            left = this._adjustedLeft;
        }

        const maxHeight = this._getMaxPopupHeight(top);
        const {isFilterActive} = this.props;

        return (
            <>
                <FaFilter
                    className={`filter-icon ${isFilterActive ? 'active' : ''}`}
                    onClick={this._toggleFilterPopupHandler}
                />
                {this._isVisible && (
                    <div ref={this._popupWrapperRef} className="filter-popup" style={{left, top, maxHeight}}>
                        <div className="filter-popup-content">
                            <div className="filter-search-wrapper" ref={this._searchWrapperRef}>
                                <div className="filter-search-row">
                                    <button
                                        className={`search-mode-btn${this._activeOperator ? ' has-operator' : ''}`}
                                        onClick={this._toggleOperatorMenu}
                                        title="Comparison mode"
                                    >
                                        <FaSearch />
                                    </button>
                                    {this._activeOperator && (
                                        <span className="active-operator-badge">{OPERATOR_LABELS[this._activeOperator]}</span>
                                    )}
                                    <input
                                        type="text"
                                        placeholder={this._activeOperator ? 'Enter value...' : 'Search...'}
                                        className="filter-search"
                                        value={this._activeOperator ? this._comparisonValue : this.props.columnSearch}
                                        onChange={this._onSearchChange}
                                    />
                                    {(this._activeOperator ? this._comparisonValue : this.props.columnSearch) && (
                                        <button className="clear-btn" onClick={this._onClearSearch}>✕</button>
                                    )}
                                </div>
                            </div>
                            {this._renderFilterOptions()}
                        </div>
                    </div>
                )}
                {this._isOperatorMenuVisible && this._operatorMenuPos && ReactDOM.createPortal(
                    <div
                        ref={this._operatorMenuRef}
                        className="operator-menu"
                        style={{
                            position: 'fixed',
                            top: this._operatorMenuPos.top,
                            left: this._operatorMenuPos.left,
                            width: this._operatorMenuPos.width
                        }}
                    >
                        {COMPARISON_OPERATORS.map(op => (
                            <button
                                key={op}
                                className={`operator-menu-item${this._activeOperator === op ? ' active' : ''}`}
                                onClick={() => this._selectOperator(op)}
                            >
                                <span className="op-symbol">{OPERATOR_LABELS[op]}</span>
                                <span>{OPERATOR_TITLES[op]}</span>
                            </button>
                        ))}
                        {this._activeOperator && (
                            <button className="operator-menu-item reset" onClick={this._resetToSearchMode}>
                                <span className="op-symbol">↺</span>
                                <span>Reset to search</span>
                            </button>
                        )}
                    </div>,
                    document.body
                )}
            </>
        );
    }

    @action.bound
    private _onSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
        if (this._activeOperator) {
            this._comparisonValue = e.target.value;
        } else {
            this.props.onChangeSearch(this.props.dataField, e.target.value);
        }
    }

    @action.bound
    private _onClearSearch() {
        if (this._activeOperator) {
            this._comparisonValue = '';
        } else {
            this.props.onChangeSearch(this.props.dataField, '');
        }
    }

    @action.bound
    private _toggleOperatorMenu(e: React.MouseEvent) {
        e.stopPropagation();
        if (!this._isOperatorMenuVisible && this._searchWrapperRef.current) {
            const rect = this._searchWrapperRef.current.getBoundingClientRect();
            this._operatorMenuPos = { top: rect.bottom + 2, left: rect.left, width: rect.width };
        }
        this._isOperatorMenuVisible = !this._isOperatorMenuVisible;
    }

    @action.bound
    private _selectOperator(op: ComparisonOperator) {
        this._activeOperator = op;
        this._comparisonValue = '';
        this._isOperatorMenuVisible = false;
    }

    @action.bound
    private _resetToSearchMode() {
        this._activeOperator = null;
        this._comparisonValue = '';
        this._isOperatorMenuVisible = false;
    }

    private _parseComparableValue(str: string): { isNumeric: boolean; num: number } {
        const cleaned = str.trim().replace(/%$/, '');
        const num = parseFloat(cleaned);
        return { isNumeric: !isNaN(num) && cleaned !== '', num };
    }

    // Parses DD.MM.YYYY (DateTimeService.toUiDate display format) → timestamp
    private _parseDateTs(str: string): number | null {
        const m = str.trim().match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
        if (!m) return null;
        const d = new Date(parseInt(m[3]), parseInt(m[2]) - 1, parseInt(m[1]));
        return isNaN(d.getTime()) ? null : d.getTime();
    }

    private _matchesComparison(optName: string, searchValue: string, operator: ComparisonOperator): boolean {
        // Date comparison takes priority — prevents "15.06.2023" being misread as numeric 15.06
        const optTs = this._parseDateTs(String(optName));
        const searchTs = this._parseDateTs(searchValue);
        if (optTs !== null && searchTs !== null) {
            switch (operator) {
                case '>':  return optTs > searchTs;
                case '<':  return optTs < searchTs;
                case '=':  return optTs === searchTs;
                case '!=': return optTs !== searchTs;
                default:   return true;
            }
        }

        // Numeric comparison (e.g. percentage values)
        const opt = this._parseComparableValue(String(optName));
        const search = this._parseComparableValue(searchValue);
        if (opt.isNumeric && search.isNumeric) {
            switch (operator) {
                case '>':  return opt.num > search.num;
                case '<':  return opt.num < search.num;
                case '=':  return opt.num === search.num;
                case '!=': return opt.num !== search.num;
                default:   return true;
            }
        }

        if (operator === '>' || operator === '<') return false;

        const a = String(optName).toLowerCase().trim();
        const b = searchValue.toLowerCase().trim();
        switch (operator) {
            case '=':  return a === b;
            case '!=': return a !== b;
            default:   return true;
        }
    }

    @action.bound
    private _toggleFilterPopupHandler(e: React.MouseEvent) {
        e.stopPropagation();
        if (this?._isVisible) {
            this._onDisappear();
            return;
        }
        this._popupEvent = e;
        this._adjustedLeft = null;
        this._isVisible = true;
        this.props.onOpen?.(this.props.dataField);
    }

    private _getMaxPopupHeight(popupTop: number) {
        return window.innerHeight - popupTop - 100;
    }

    private get _filterOptions() {
        const options = (typeof this.props.filterOptions === 'function' ? this.props.filterOptions() : this.props.filterOptions) || [];

        if (this.props.hasEmptyValues) {
            const defaultOptions = options.length > 0
                ? [
                    {name: 'With values', value: ColumnFilters.WithValues},
                    {name: 'Without values', value: ColumnFilters.WithoutValues}
                  ]
                : [{name: 'Without values', value: ColumnFilters.WithoutValues}];
            return [...defaultOptions, ...options];
        }

        return [...options];
    }

    private get _filteredOptions(): FilterOption[] {
        if (this._activeOperator) {
            const compValue = this._comparisonValue.trim();
            if (!compValue) return this._filterOptions;

            return this._filterOptions.reduce<FilterOption[]>((acc, opt: FilterOption) => {
                if (!opt.isGroup) {
                    if (this._matchesComparison(String(opt.name), compValue, this._activeOperator!)) acc.push(opt);
                    return acc;
                }
                const filteredChildren = opt?.children?.reduce<FilterOption[]>((childAcc, child) => {
                    if (!child.isGroup) {
                        if (this._matchesComparison(String(child.name), compValue, this._activeOperator!)) childAcc.push(child);
                        return childAcc;
                    }
                    const matchingDays = child.children?.filter(day =>
                        this._matchesComparison(String(day.name), compValue, this._activeOperator!)
                    ) ?? [];
                    if (matchingDays.length > 0) childAcc.push({...child, children: matchingDays} as FilterOption);
                    return childAcc;
                }, []);
                if (filteredChildren && filteredChildren.length > 0) acc.push({...opt, children: filteredChildren} as FilterOption);
                return acc;
            }, []);
        }

        const terms = gridStringSearchService.splitSearchTerms(this.props.columnSearch);

        if (!terms.length) return this._filterOptions;

        const matches = (name: FilterOption['name']) => gridStringSearchService.matchesAnyTerm(String(name), terms);

        return this._filterOptions.reduce<FilterOption[]>((acc, opt: FilterOption) => {
            if (!opt.isGroup) {
                if (matches(opt.name)) acc.push(opt);
                return acc;
            }

            const filteredChildren = opt?.children?.reduce<FilterOption[]>((childAcc, child) => {
                if (!child.isGroup) {
                    if (matches(child.name)) childAcc.push(child);
                    return childAcc;
                }
                const matchingDays = child.children?.filter(day => matches(day.name)) ?? [];
                if (matchingDays.length > 0) {
                    childAcc.push({...child, children: matchingDays} as FilterOption);
                }
                return childAcc;
            }, []);

            if (filteredChildren && filteredChildren?.length > 0) {
                acc.push({...opt, children: filteredChildren} as FilterOption);
            }
            return acc;
        }, []);
    }



    private _renderText = (text: string | undefined) => {
        if (!this.props.columnSearch) return text;
        const textComparer: TextMatchComparer = (v, t, s) => stringSearchService.prepareString(v)?.indexOf(stringSearchService.prepareString(t) ?? '', s) ?? -1;
        const parts = gridStringSearchService.markMatches(text, gridStringSearchService.splitSearchTerms(this.props.columnSearch), textComparer);
        return (
            <>
                {parts.map((p, idx) =>
                    p.isMatch ? <span className="text-match" key={idx}>{p.text}</span> : p.text
                )}
            </>
        );
    };

    @action.bound
    private _toggleGroup(value: string) {
        if (this._expandedGroups.has(value)) {
            this._expandedGroups.delete(value);
        } else {
            this._expandedGroups.add(value);
        }
    }

    private _renderFilterOptions() {
        const { columnFilters, dataField, onChangeFilters } = this.props;
        const options = this._filteredOptions;

        if (!options.length) return null;

        const isGrouped = options.some((o: FilterOption) => o?.isGroup);
        if (isGrouped) {
            const flatOptions = options.filter(o => !o.isGroup);
            const groupedOptions = options.filter(o => o.isGroup) as FilterOption[];

            return (
                <div className="filter-options-grouped">
                    {flatOptions.map(opt => (
                        <Label key={String(opt.value)} className="filter-item-wrapper">
                            <Checkbox
                                style={{ width: 'unset', height: 'unset' }}
                                checked={!!columnFilters?.get(dataField)?.includes(opt.value)}
                                onChange={() => onChangeFilters(dataField, opt.value)}
                            />
                            <span>{this._renderText(String(opt.name))}</span>
                        </Label>
                    ))}
                    {groupedOptions.map(yearOpt => {
                        const yearKey = String(yearOpt.value);
                        const yearExpanded = !!this.props.columnSearch || this._expandedGroups.has(yearKey);
                        const yearValues: FilterValue[] = (yearOpt.children ?? []).reduce<FilterValue[]>((acc, m) => {
                            (m.isGroup ? (m.children ?? []) : [m]).forEach((d: FilterOption) => acc.push(d.value));
                            return acc;
                        }, []);
                        const yearSelectedCount = yearValues.filter((v: FilterValue) => columnFilters?.get(dataField)?.includes(v)).length;
                        const yearChecked = yearValues.length > 0 && yearSelectedCount === yearValues.length;
                        const yearIndeterminate = yearSelectedCount > 0 && yearSelectedCount < yearValues.length;
                        return (
                            <div key={`${yearOpt.value}`} className="filter-group-year">
                                <div className="filter-group-header">
                                    <GroupCheckbox
                                        checked={yearChecked}
                                        indeterminate={yearIndeterminate}
                                        onChange={() => this.props.onChangeManyFilters(dataField, yearValues, !yearChecked)}
                                    />
                                    <div className="filter-group-toggle" onClick={() => this._toggleGroup(yearKey)}>
                                        <span>{yearExpanded ? '▾' : '▸'}</span>
                                        <span>{yearOpt.name}</span>
                                        <span className="filter-group-count">
                                        ({yearOpt.children?.reduce((acc, m: FilterOption) => acc + (m?.children?.length ?? 0), 0)})
                                    </span>
                                    </div>
                                </div>
                                {yearExpanded && yearOpt.children?.map(monthOpt => {
                                    if (!monthOpt.isGroup) return null;
                                    const monthKey = String(monthOpt.value);
                                    const monthExpanded = !!this.props.columnSearch || this._expandedGroups.has(monthKey);
                                    const monthValues = monthOpt.children?.map(d => d.value) ?? [];
                                    const monthSelectedCount = monthValues.filter(v => columnFilters?.get(dataField)?.includes(v)).length;
                                    const monthChecked = monthValues.length > 0 && monthSelectedCount === monthValues.length;
                                    const monthIndeterminate = monthSelectedCount > 0 && monthSelectedCount < monthValues.length;
                                    return (
                                        <div key={`${monthOpt.value}`} className="filter-group-month">
                                            <div className="filter-group-header">
                                                <GroupCheckbox
                                                    checked={monthChecked}
                                                    indeterminate={monthIndeterminate}
                                                    onChange={() => this.props.onChangeManyFilters(dataField, monthValues, !monthChecked)}
                                                />
                                                <div className="filter-group-toggle" onClick={() => this._toggleGroup(monthKey)}>
                                                    <span>{monthExpanded ? '▾' : '▸'}</span>
                                                    <span>{monthOpt.name}</span>
                                                    <span className="filter-group-count">
                                                ({monthOpt.children?.length ?? 0})
                                            </span>
                                                </div>
                                            </div>
                                            {monthExpanded && monthOpt.children?.map(dayOpt => (
                                                <Label key={dayOpt.value as string} className="filter-item-wrapper filter-item-day">
                                                    <Checkbox
                                                        style={{ width: 'unset', height: 'unset' }}
                                                        checked={!!columnFilters?.get(dataField)?.includes(dayOpt.value)}
                                                        onChange={() => onChangeFilters(dataField, dayOpt.value)}
                                                    />
                                                    <span>{this._renderText(String(dayOpt.name))}</span>
                                                </Label>
                                            ))}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            );
        }

        const itemHeight = 24;
        const height = Math.min(options.length * itemHeight, 300);

        return (
            <List
                height={height}
                itemCount={options.length}
                itemSize={itemHeight}
                width="100%"
                itemData={{
                    options,
                    columnFilters,
                    dataField,
                    onChangeFilters,
                    onRenderText: this._renderText
                }}
            >
                {Row}
            </List>
        );
    }
}

type RowData = {
    options: FilterOption[];
    columnFilters: ColumnFiltersMap;
    dataField: string;
    onChangeFilters: (key: string, filterValue: FilterValue) => void;
    onRenderText: (name: string) => React.ReactNode;
};

const Row = observer(({ index, style, data }: ListChildComponentProps<RowData>) => {
    const { options, columnFilters, dataField, onChangeFilters, onRenderText } = data;
    const f = options[index];

    const isChecked = !!columnFilters?.get(dataField)?.includes(f.value);

    return (
        <div style={style}>
            <Label className="filter-item-wrapper">
                <Checkbox
                    style={{ width: 'unset', height: 'unset' }}
                    checked={isChecked}
                    onChange={() => onChangeFilters(dataField, f.value)}
                />
                <span>{onRenderText(f.name.toString())}</span>
            </Label>
        </div>
    );
});